import { useState, useCallback, useMemo } from 'react';
import {
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  Database,
  X,
} from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { classifyError } from '@/lib/errors';
import { fullDate, relativeTime } from '@/lib/datetime';
import { useExpandSet } from '@/hooks/useExpandSet';
import type { ChangeEventOut } from '@/api/changeHistory';
import {
  useChangeHistoryQuery,
  useChangeEventDetails,
  useChangeActorNames,
  getActorDisplayName,
} from '@/hooks/useChangeHistory';
import {
  ChangeHistoryFilterBar,
  auditPeriodToStart,
  type AuditPeriod,
} from './ChangeHistoryFilterBar';
import { ChangeDetailTable } from './ChangeDetailTable';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const AUTO_REFRESH_MS = 30_000;
const CARD_BORDER = 'border border-gray-300 dark:border-gray-700';

/* ------------------------------------------------------------------ */
/*  Badges                                                             */
/* ------------------------------------------------------------------ */

const ACTION_STYLES: Record<string, string> = {
  CREATE: 'bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-400',
  UPDATE: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
};

function ActionBadge({ action }: { action: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[11px] font-semibold leading-none ${ACTION_STYLES[action] || 'bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400'}`}>
      {action}
    </span>
  );
}

function TableBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400">
      {label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Detail item                                                        */
/* ------------------------------------------------------------------ */

function DetailItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="text-xs">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Change event card (lazy-loads details on expand)                    */
/* ------------------------------------------------------------------ */

function ChangeEventCard({
  item,
  expanded,
  onToggle,
  actorName,
}: {
  item: ChangeEventOut;
  expanded: boolean;
  onToggle: () => void;
  actorName?: string;
}) {
  const { data: eventDetails, isLoading: detailsLoading } = useChangeEventDetails(
    item.id,
    expanded,
  );

  return (
    <div className={`${CARD_BORDER} rounded-lg bg-[var(--color-bg-paper)]`}>
      {/* Collapsed row */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 text-left sm:gap-3"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="shrink-0 cursor-help whitespace-nowrap font-mono text-xs text-muted-foreground">
              {relativeTime(item.created_at)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top"><span className="text-xs">{fullDate(item.created_at)}</span></TooltipContent>
        </Tooltip>
        <TableBadge label={item.table_label} />
        <ActionBadge action={item.action} />
        <span className="hidden font-mono text-xs text-muted-foreground sm:inline">
          {item.record_id}
        </span>
        {actorName && (
          <span className="hidden break-words text-xs text-muted-foreground md:inline">
            {actorName}
          </span>
        )}
        <span className="ml-auto">
          {expanded
            ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            : <ChevronDown className="h-4 w-4 shrink-0 rotate-[-90deg] text-muted-foreground" />}
        </span>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-[var(--color-divider)] px-3 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
            <DetailItem label="Data e hora">
              <span className="font-mono text-xs">{fullDate(item.created_at)}</span>
            </DetailItem>
            <DetailItem label="Tabela">
              <span className="font-mono text-xs">{item.table_name}</span>
            </DetailItem>
            <DetailItem label="Record ID">
              <span className="inline-flex items-center gap-1">
                <span className="font-mono text-xs">{item.record_id}</span>
                <CopyButton text={item.record_id} />
              </span>
            </DetailItem>
            <DetailItem label="IP">
              <span className="font-mono text-xs">{item.ip || '—'}</span>
            </DetailItem>
            {item.request_id && (
              <DetailItem label="Request ID">
                <span className="inline-flex items-center gap-1">
                  <span className="font-mono text-xs">{item.request_id}</span>
                  <CopyButton text={item.request_id} />
                </span>
              </DetailItem>
            )}
            {actorName && (
              <DetailItem label="Ator">
                <span className="text-xs">{actorName}</span>
              </DetailItem>
            )}
          </div>

          {/* Field-level changes */}
          <div className="space-y-1">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Mudanças
            </span>
            {detailsLoading ? (
              <div className="flex justify-center py-4">
                <Spinner size={16} label="Carregando detalhes" />
              </div>
            ) : eventDetails ? (
              <ChangeDetailTable details={eventDetails.details} />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main tab component                                                 */
/* ------------------------------------------------------------------ */

interface ChangeHistoryTabProps {
  autoRefresh: boolean;
  initialRequestId?: string;
}

export function ChangeHistoryTab({ autoRefresh, initialRequestId }: ChangeHistoryTabProps) {
  const [table, setTable] = useState('__all__');
  const [recordId, setRecordId] = useState('');
  const [action, setAction] = useState('__all__');
  const [period, setPeriod] = useState<AuditPeriod>('24h');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [requestIdFilter, setRequestIdFilter] = useState(initialRequestId || '');

  const { expandedSet, toggle, expandAll, collapseAll } = useExpandSet();

  const resetPage = useCallback(() => setPage(1), []);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      data_inicio: auditPeriodToStart(period),
      page,
      page_size: pageSize,
    };
    if (table !== '__all__') p.table = table;
    if (recordId.trim()) p.record_id = recordId.trim();
    if (action !== '__all__') p.action = action;
    if (requestIdFilter) p.request_id = requestIdFilter;
    return p;
  }, [table, recordId, action, period, page, pageSize, requestIdFilter]);

  const { data, isLoading, error } = useChangeHistoryQuery(
    params,
    true,
    autoRefresh ? AUTO_REFRESH_MS : false,
  );

  const actorIds = useMemo(
    () => data?.items.map((i) => i.actor_id) ?? [],
    [data],
  );
  const { data: actorNames } = useChangeActorNames(actorIds);

  return (
    <div className="space-y-4 pt-4">
      {!requestIdFilter && (
        <ChangeHistoryFilterBar
          table={table}
          onTableChange={(v) => { setTable(v); resetPage(); }}
          recordId={recordId}
          onRecordIdChange={(v) => { setRecordId(v); resetPage(); }}
          action={action}
          onActionChange={(v) => { setAction(v); resetPage(); }}
          period={period}
          onPeriodChange={(v) => { setPeriod(v); resetPage(); }}
        />
      )}

      {requestIdFilter && (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-400">
          <span>Filtrando por request_id: <code className="font-mono">{requestIdFilter}</code></span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
            onClick={() => { setRequestIdFilter(''); resetPage(); }}
            title="Limpar filtro"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner size={32} label="Carregando histórico" />
        </div>
      )}

      {!isLoading && data && data.items.length === 0 && (
        <EmptyState
          icon={<Database className="h-10 w-10" />}
          title="Nenhuma mudança encontrada"
          description="Ajuste os filtros ou o período para encontrar registros de change tracking."
        />
      )}

      {!isLoading && data && data.items.length > 0 && (
        <>
          <div className="flex items-center justify-end">
            <div className="flex shrink-0 gap-1">
              <Button variant="ghost" size="sm" onClick={() => expandAll(data.items.length)} title="Expandir todos">
                <ChevronsUpDown className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={collapseAll} title="Colapsar todos">
                <ChevronsDownUp className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            {data.items.map((item, idx) => (
              <ChangeEventCard
                key={item.id}
                item={item}
                expanded={expandedSet.has(idx)}
                onToggle={() => toggle(idx)}
                actorName={getActorDisplayName(item.actor_id, actorNames)}
              />
            ))}
          </div>

          <DataTablePagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            pages={data.pages}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); resetPage(); }}
            pageSizeOptions={[50, 100, 200]}
          />
        </>
      )}
    </div>
  );
}
